import React from 'react';
import { formatMoney as formatMoneyUtil, parseMoney } from '../../utils/format';
import {
    CheckCircle2,
    User,
    Building2,
    Calendar,
    DollarSign,
    CreditCard,
    AlertCircle,
    ArrowRightLeft,
    TrendingDown,
    Banknote,
    Clock,
    Tag,
    MessageSquare,
} from 'lucide-react';

interface FunctionalUnitForSale {
    id: number;
    projectName: string;
    functionalUnitName: string;
    ufType: string;
    totalSurface: number;
    listPrice: number;
}

interface PaymentMethod {
    id: string;
    currency: 'ARS' | 'USD';
    paymentType: 'cash' | 'financing';
    accountingType: 'A' | 'B';
    description: string;
    amount: string;
    iva: string;
    exchangeRate: string;
    paymentDate?: string;
    financingType?: string;
    firstInstallmentDate?: string;
    installments?: number;
    updateIndex?: string;
    indexBaseDate?: string;
}

interface Step4SummaryProps {
    clientName: string;
    functionalUnits: FunctionalUnitForSale[];
    currency: 'ARS' | 'USD';
    exchangeRate: string;
    total: number;
    saleDate: string;
    comments: string;
    paymentMethods: PaymentMethod[];
    closedPrices: { [key: number]: string };
}

const Row: React.FC<{ label: string; value: React.ReactNode; muted?: boolean }> = ({ label, value, muted }) => (
    <div className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className={`text-sm font-medium ${muted ? 'text-muted-foreground' : 'text-foreground'}`}>{value}</span>
    </div>
);

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string; count?: number }> = ({ icon, title, count }) => (
    <div className="flex items-center gap-2 mb-3">
        <div className="w-4 h-4 text-primary flex items-center justify-center">{icon}</div>
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex-1">{title}</h3>
        {count !== undefined && (
            <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{count}</span>
        )}
    </div>
);

const financingLabels: { [key: string]: string } = {
    libre: 'Libre',
    cuotas_mensuales: 'Cuotas Mensuales',
    cuotas_bimensuales: 'Cuotas Bimensuales',
    cuotas_trimestrales: 'Cuotas Trimestrales',
    cuotas_cuatrimestrales: 'Cuotas Cuatrimestrales',
    cuotas_pentamestrales: 'Cuotas Pentamestrales',
    cuotas_semestrales: 'Cuotas Semestrales',
    cuotas_anuales: 'Cuotas Anuales',
};

const Step4Summary: React.FC<Step4SummaryProps> = ({
    clientName,
    functionalUnits,
    currency,
    exchangeRate,
    total,
    saleDate,
    comments,
    paymentMethods,
    closedPrices
}) => {
    const fmt = (value: number | string) => {
        const num = typeof value === 'string' ? (parseMoney(value) ?? 0) : value;
        return formatMoneyUtil(num);
    };

    const fmtDate = (dateString?: string) => {
        if (!dateString) return '—';
        return new Date(dateString + 'T00:00:00').toLocaleDateString('es-AR', {
            day: '2-digit', month: 'short', year: 'numeric'
        });
    };

    const exchangeRateNum = parseFloat(exchangeRate) || 0;

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                    <h2 className="text-base font-bold text-foreground">Resumen de Venta</h2>
                    <p className="text-xs text-muted-foreground">Revise la información antes de confirmar</p>
                </div>
            </div>

            {/* General */}
            <div className="bg-card border border-border rounded-xl p-4">
                <SectionTitle icon={<Tag className="w-4 h-4" />} title="Información General" />
                <Row label="Cliente" value={
                    <span className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-muted-foreground" />
                        {clientName || '—'}
                    </span>
                } />
                <Row label="Fecha de Boleto" value={fmtDate(saleDate)} />
                <Row label="Moneda" value={
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                        currency === 'USD'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}>{currency}</span>
                } />
                {currency === 'ARS' && exchangeRate && (
                    <Row label="Tipo de Cambio" value={
                        <span className="flex items-center gap-1">
                            <ArrowRightLeft className="w-3 h-3 text-muted-foreground" />
                            ${parseFloat(exchangeRate).toLocaleString('es-AR')} ARS/USD
                        </span>
                    } />
                )}
                <Row label="Total de Venta" value={
                    <span className="text-base font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                        {currency} {fmt(total)}
                    </span>
                } />
            </div>

            {/* Unidades Funcionales */}
            <div className="bg-card border border-border rounded-xl p-4">
                <SectionTitle
                    icon={<Building2 className="w-4 h-4" />}
                    title="Unidades Funcionales"
                    count={functionalUnits.length}
                />
                {functionalUnits.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">Sin unidades seleccionadas</p>
                ) : (
                    <div>
                        {functionalUnits.map((unit) => {
                            const listPriceInCurrency = currency === 'ARS' && exchangeRateNum > 0
                                ? unit.listPrice * exchangeRateNum : unit.listPrice;
                            const closedNum = closedPrices[unit.id] ? parseMoney(closedPrices[unit.id]) : null;
                            const finalPrice = closedNum ?? listPriceInCurrency;
                            const hasDiscount = closedNum !== null && closedNum < listPriceInCurrency;
                            const discountPct = hasDiscount && listPriceInCurrency > 0
                                ? ((listPriceInCurrency - closedNum!) / listPriceInCurrency * 100).toFixed(1) : null;

                            return (
                                <div key={unit.id} className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0 gap-3">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-foreground truncate">{unit.functionalUnitName}</p>
                                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                            {unit.projectName} · {unit.ufType}{unit.totalSurface ? ` · ${unit.totalSurface}m²` : ''}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0 space-y-0.5">
                                        {hasDiscount && (
                                            <p className="text-xs text-muted-foreground/50 line-through tabular-nums">{currency} {fmt(listPriceInCurrency)}</p>
                                        )}
                                        <p className="text-sm font-semibold text-foreground tabular-nums">{currency} {fmt(finalPrice)}</p>
                                        {discountPct && (
                                            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                                <TrendingDown className="w-2.5 h-2.5" />{discountPct}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <div className="flex items-center justify-between pt-3 mt-1">
                            <span className="text-sm font-medium text-muted-foreground">Total</span>
                            <span className="text-sm font-bold text-foreground tabular-nums">{currency} {fmt(total)}</span>
                        </div>
                    </div>
                )}
                {comments && (
                    <div className="mt-4 pt-3 border-t border-border/50 flex items-start gap-2">
                        <MessageSquare className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Observaciones</p>
                            <p className="text-sm text-foreground leading-relaxed">{comments}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Métodos de Pago */}
            <div className="bg-card border border-border rounded-xl p-4">
                <SectionTitle
                    icon={<CreditCard className="w-4 h-4" />}
                    title="Métodos de Pago"
                    count={paymentMethods.length}
                />
                {paymentMethods.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">Sin métodos de pago</p>
                ) : (
                    <div className="space-y-3">
                        {paymentMethods.map((method, index) => {
                            const isCash = method.paymentType === 'cash';
                            const ivaNum = parseFloat(method.iva) || 0;
                            return (
                                <div key={method.id} className="rounded-lg border border-border overflow-hidden">
                                    {/* Method header */}
                                    <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-muted/30 border-b border-border/50">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className="w-6 h-6 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                                                {isCash
                                                    ? <Banknote className="w-3.5 h-3.5 text-muted-foreground" />
                                                    : <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                                                }
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-foreground truncate">
                                                    {method.description || `Método ${index + 1}`}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    {isCash ? 'Efectivo' : 'Financiado'} · Tipo {method.accountingType} · {method.currency}
                                                </p>
                                            </div>
                                        </div>
                                        <p className="text-sm font-semibold text-foreground tabular-nums shrink-0">
                                            {method.currency} {fmt(method.amount)}
                                        </p>
                                    </div>

                                    {/* Detail rows */}
                                    <div className="px-3">
                                        {method.accountingType === 'A' && ivaNum > 0 && (
                                            <Row label="IVA" value={`${ivaNum}%`} muted />
                                        )}
                                        {isCash && method.paymentDate && (
                                            <Row label="Fecha de pago" value={fmtDate(method.paymentDate)} muted />
                                        )}
                                        {!isCash && (
                                            <>
                                                <Row label="Tipo de financiación" value={financingLabels[method.financingType || ''] || method.financingType || '—'} muted />
                                                <Row label="Cuotas" value={`${method.installments}`} muted />
                                                <Row label="Primera cuota" value={fmtDate(method.firstInstallmentDate)} muted />
                                                {method.updateIndex && (
                                                    <Row label="Índice" value={method.updateIndex} muted />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Notice */}
            <div className="flex items-start gap-2.5 p-3.5 rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10">
                <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-300">
                    Verifique que toda la información sea correcta. Puede volver a pasos anteriores antes de confirmar.
                </p>
            </div>
        </div>
    );
};

export default Step4Summary;
